/**
 * The kiosk's route table — the SINGLE list both apps build their navigation from.
 *
 * The kiosk uses real routes on each platform (react-router on web, a nested
 * native-stack on mobile) rather than one screen switching on state. That was a
 * deliberate call; the cost is two navigation implementations, and this file plus
 * `packages/ui/kiosk/navigation/KioskNavContext` are what stop them drifting.
 *
 * Shared screens NEVER import a router. They call `useKioskNav().go(KIOSK_ROUTES.CART)`
 * and each app supplies the implementation.
 *
 * Item customisation is deliberately absent: it is a MODAL over the catalog route, not
 * a route. Putting it in the table would let a customer deep-link into a half-built
 * line item.
 *
 * See docs/KIOSK_ARCHITECTURE.md.
 */

export const KIOSK_ROUTES = {
  IDLE: "IDLE",
  SERVICE: "SERVICE",
  CATALOG: "CATALOG",
  /**
   * The dedicated search screen. A ROUTE, not a mode of CATALOG: it is a whole screen with
   * its own chrome (a full-width bar with a back arrow, no rail, no banner, no chips) and
   * its own empty states, so a customer arrives at it and leaves it rather than watching
   * the catalog rearrange itself underneath them.
   *
   * CATALOG keeps its own `browse`/`search` split all the same — `catalogMode` still
   * answers for a query committed to `kioskStore` while that screen is mounted, which is
   * what a back-navigation out of here with a live term produces.
   */
  SEARCH: "SEARCH",
  CART: "CART",
  PAYMENT: "PAYMENT",
  SUCCESS: "SUCCESS",
  /**
   * The staff session behind the kiosk died. A DEAD END: no timer, no actions, and
   * nothing routes out of it — only staff, through the hidden exit. See §13.10.
   */
  SESSION_EXPIRED: "SESSION_EXPIRED",
} as const;

export type KioskRoute = (typeof KIOSK_ROUTES)[keyof typeof KIOSK_ROUTES];

/**
 * Web paths, RELATIVE to the parent `/kiosk` route. IDLE is the index route.
 * Mobile ignores these and uses the route key as the stack screen name.
 */
export const KIOSK_ROUTE_PATHS: Record<KioskRoute, string> = {
  [KIOSK_ROUTES.IDLE]: "",
  [KIOSK_ROUTES.SERVICE]: "service",
  [KIOSK_ROUTES.CATALOG]: "catalog",
  [KIOSK_ROUTES.SEARCH]: "search",
  [KIOSK_ROUTES.CART]: "cart",
  [KIOSK_ROUTES.PAYMENT]: "payment",
  [KIOSK_ROUTES.SUCCESS]: "success",
  [KIOSK_ROUTES.SESSION_EXPIRED]: "session-expired",
};

/** Declaration order — also the order screens are registered in both apps. */
export const KIOSK_ROUTE_ORDER: KioskRoute[] = [
  KIOSK_ROUTES.IDLE,
  KIOSK_ROUTES.SERVICE,
  KIOSK_ROUTES.CATALOG,
  KIOSK_ROUTES.SEARCH,
  KIOSK_ROUTES.CART,
  KIOSK_ROUTES.PAYMENT,
  KIOSK_ROUTES.SUCCESS,
  KIOSK_ROUTES.SESSION_EXPIRED,
];

/**
 * The session mode each route belongs to. DERIVED, never stored — §3 of the
 * architecture doc originally specified a `mode` field alongside the route, and two
 * fields that can disagree is one field too many. Timeout policy keys off this.
 */
export type KioskMode = "idle" | "ordering" | "paying" | "success" | "expired";

export const MODE_BY_ROUTE: Record<KioskRoute, KioskMode> = {
  [KIOSK_ROUTES.IDLE]: "idle",
  [KIOSK_ROUTES.SERVICE]: "ordering",
  [KIOSK_ROUTES.CATALOG]: "ordering",
  [KIOSK_ROUTES.SEARCH]: "ordering",
  [KIOSK_ROUTES.CART]: "ordering",
  [KIOSK_ROUTES.PAYMENT]: "paying",
  [KIOSK_ROUTES.SUCCESS]: "success",
  // Its own mode, not "idle": both have no timer, but an idle kiosk is waiting for a
  // customer and an expired one cannot take an order at all.
  [KIOSK_ROUTES.SESSION_EXPIRED]: "expired",
};

/**
 * Is this location inside the kiosk?
 *
 * Takes the base path as an ARGUMENT rather than importing it, so this file stays a pure
 * table with no platform dependency — the base is `ROUTE_CONSTANTS_MAP.KIOSK_ROUTE` on web
 * and `SCREEN_NAMES.Kiosk` (the stack screen name) on mobile.
 *
 * Accepts a string or a destination-shaped object, because each platform's guard is handed
 * more than one form:
 *
 * | caller | shape |
 * |---|---|
 * | web `history.push("/x")` | `string` |
 * | web `history.push({ pathname: "/x" })` | `{ pathname }` |
 * | mobile `NavigationUtils.navigate("NewOrder")` | `string` |
 * | mobile `NavigationUtils.reset([{ name: "Login" }])` | `{ name }`, one per entry |
 *
 * `pathname` is read before `name`; no caller supplies both.
 */
export function isKioskPath(
  to: string | { pathname?: string; name?: string } | null | undefined,
  kioskBase: string,
): boolean {
  const path = typeof to === "string" ? to : to?.pathname || to?.name || "";
  return path.startsWith(kioskBase);
}

/**
 * Is a whole-stack RESET entirely inside the kiosk? Mobile only — web's history has no
 * equivalent operation.
 *
 * ⚠️ Every entry has to be inside, not just the focused one. `NavigationUtils.reset` rebuilds
 * the stack, so a POS screen left ANYWHERE in it is one back gesture from a customer even if
 * the kiosk is what renders first.
 *
 * An empty or malformed reset is NOT inside the kiosk. That answer is the safe one in both
 * directions: while kiosk mode is on the guard blocks it, and while the mode is off nothing
 * consults this at all.
 */
export function isKioskResetTarget(
  routes: Array<{ name?: string }> | null | undefined,
  kioskBase: string,
): boolean {
  if (!Array.isArray(routes) || routes.length === 0) return false;
  return routes.every((route) => isKioskPath(route, kioskBase));
}

/**
 * Should the app redirect to the kiosk right now?
 *
 * The RENDER-TIME half of the one-way door (§13.10). The history guard stops out-of-React
 * `push`/`replace` before the URL ever changes, but three things get past it because they
 * never call either method:
 *
 *  - a browser BACK (popstate) — history v5 applies it and only then notifies listeners;
 *  - a cold boot in kiosk mode, where the app starts at whatever route the login flow picks;
 *  - a stale bookmark or a deep link straight into a POS route.
 *
 * Answering it at RENDER time closes all three at once, and closes them better than an
 * interception could: the POS route tree is never rendered at all, so `HomeLayout` and its
 * notification polling and cash-register modals do not mount for even one frame in front of
 * a customer.
 */
export function shouldLockToKiosk(
  kioskModeEnabled: boolean | undefined,
  pathname: string,
  kioskBase: string,
): boolean {
  return Boolean(kioskModeEnabled) && !isKioskPath(pathname, kioskBase);
}

/**
 * What should a hardware BACK press do? Mobile only — the web door has no equivalent,
 * because a browser back cannot be intercepted at all (history v5 applies a POP and only
 * then notifies listeners, which is why web needs the render-time `shouldLockToKiosk`
 * bounce instead).
 *
 * Android CAN be intercepted, so the mobile door closes at the press rather than after it:
 * the kiosk is never popped off the POS stack in the first place, and no POS screen mounts
 * for even a frame. That is the same guarantee `KioskLock` gives web, reached the other way
 * round.
 *
 *  - `ignore`  — kiosk mode is off. The POS's own back behaviour is completely untouched.
 *  - `back`    — move back INSIDE the kiosk. Mirrors web, where a browser back between two
 *                kiosk routes is an ordinary navigation and only leaving is bounced.
 *  - `consume` — swallow it. The customer is at the kiosk's root (or on the SESSION_EXPIRED
 *                dead end); the only thing back could do is hand them the cashier's POS.
 *
 * ⚠️ `back` and `consume` are BOTH handled here rather than one of them being delegated by
 * returning `false` from the listener. Falling through would rely on the nested kiosk
 * `NavigationContainer` having registered a back handler of its own, and on RN invoking the
 * listeners in exactly the reverse registration order that puts it next in line — an
 * assumption about two libraries' internals whose failure mode is the whole hole this
 * closes. Deciding both cases here needs neither.
 */
export type HardwareBackPlan = "ignore" | "back" | "consume";

export function planHardwareBack(
  kioskModeEnabled: boolean | undefined,
  canGoBackInsideKiosk: boolean,
): HardwareBackPlan {
  if (!kioskModeEnabled) return "ignore";
  return canGoBackInsideKiosk ? "back" : "consume";
}
